import { useState, useMemo } from 'react';
import { ItemSlot }    from '../atoms/ItemSlot';
import { getSellPrice } from '../../data/items';

// Filter tabs in display order — runes are excluded (they live in the Runes view)
const FILTER_TABS = [
  { id: 'all',    label: 'All'    },
  { id: 'weapon', label: '🗡️ Weapon' },
  { id: 'helmet', label: '🪖 Helmet' },
  { id: 'armor',  label: '🛡️ Armor'  },
  { id: 'ring',   label: '💍 Ring'   },
  { id: 'gloves', label: '🧤 Gloves' },
  { id: 'boots',  label: '👢 Boots'  },
  { id: 'amulet', label: '📿 Amulet' },
  { id: 'set',    label: '◆ Set'    },
];

// Rarity sort order: legendary first, nulls last
const RARITY_ORDER = { legendary: 0, set: 1, epic: 2, rare: 3, uncommon: 4, common: 5 };

/**
 * 10 × 20 grid of item slots (200 total).
 *
 * Features:
 *  - Filter bar — show only items of a given slot type.
 *    Non-matching filled slots render as dimmed empty-ish cells.
 *  - Sort by rarity / level — toggle buttons reorder the display.
 *  - Sell mode — toggle button turns clicks into sell actions.
 *    Each matching item shows its gold sell value as a price badge.
 *  - Sell All — visible in sell mode; sells every item matching the active filter.
 *    Requires a confirmation click to prevent accidents.
 *  - Lock mode — toggle button turns clicks into lock/unlock actions.
 *    Locked items show a 🔐 badge and are immune to selling (including Sell All).
 *
 * Props:
 *  inventory   — array[200] of item | null
 *  onEquip(item, index)  — equip an item
 *  onSell(item, index)   — sell an item (credits gold to caller)
 *  onSellAll(slotTypeFilter) — sell all items matching the filter
 *  onLock(index)         — toggle lock on item at index
 *  totalGold             — shown in sell mode header so user sees balance
 *  playerLevel           — meta player level; items above this req are shown locked
 */
export function InventoryGrid({ inventory, onEquip, onSell, onSellAll, onLock, totalGold, playerLevel = 1, equipped = {} }) {
  // Build a map of { setId: count } from currently equipped items
  const setCountMap = useMemo(() => {
    const map = {};
    const mainSlots = ['weapon', 'helmet', 'armor', 'ring1', 'ring2', 'gloves', 'boots', 'amulet'];
    for (const slotId of mainSlots) {
      const item = equipped[slotId];
      if (item?.setId) {
        map[item.setId] = (map[item.setId] ?? 0) + 1;
      }
    }
    return map;
  }, [equipped]);
  const [activeFilters,  setActiveFilters]  = useState(() => new Set(['all']));
  const [sellMode,       setSellMode]       = useState(false);
  const [lockMode,       setLockMode]       = useState(false);
  const [sortMode,       setSortMode]       = useState('none'); // 'none' | 'rarity' | 'level'
  const [confirmSellAll, setConfirmSellAll] = useState(false);

  // Runes live in their own view — exclude them from item count and grid
  const filled = inventory.filter(item => item && item.slotType !== 'rune').length;

  // ── Build a display-order mapping [{ item, origIdx }] ─────────────────────
  // Sorting is purely visual — origIdx is always passed to onEquip/onSell so
  // the underlying inventory array is never mutated by the sort toggle.
  const displayItems = useMemo(() => {
    const pairs = inventory.map((item, i) => ({ item, origIdx: i }));
    if (sortMode === 'rarity') {
      return [...pairs].sort((a, b) => {
        if (!a.item && !b.item) return 0;
        if (!a.item) return 1;
        if (!b.item) return -1;
        return (RARITY_ORDER[a.item.rarity] ?? 5) - (RARITY_ORDER[b.item.rarity] ?? 5);
      });
    }
    if (sortMode === 'level') {
      return [...pairs].sort((a, b) => {
        if (!a.item && !b.item) return 0;
        if (!a.item) return 1;
        if (!b.item) return -1;
        return (b.item.levelReq ?? 1) - (a.item.levelReq ?? 1); // highest level first
      });
    }
    return pairs;
  }, [inventory, sortMode]);

  // ── Always pack items to top-left, filtering out runes and non-matching slots ─
  const packedDisplayItems = useMemo(() => {
    const matching = displayItems.filter(({ item }) => {
      if (!item || item.slotType === 'rune') return false;
      if (activeFilters.has('all')) return true;
      if (activeFilters.has('set') && item.rarity === 'set') return true;
      return activeFilters.has(item.slotType);
    });
    const padCount = displayItems.length - matching.length;
    return [
      ...matching,
      ...Array.from({ length: padCount }, () => ({ item: null, origIdx: null })),
    ];
  }, [displayItems, activeFilters]);

  // ── Toggle sort mode (cycling through options) ─────────────────────────────
  const toggleSort = (mode) => setSortMode(prev => prev === mode ? 'none' : mode);

  // ── Toggle sell mode (also resets confirm state, exits lock mode) ──────────
  const toggleSellMode = () => {
    setSellMode(v => !v);
    setLockMode(false);
    setConfirmSellAll(false);
  };

  // ── Toggle lock mode (exits sell mode) ─────────────────────────────────────
  const toggleLockMode = () => {
    setLockMode(v => !v);
    setSellMode(false);
    setConfirmSellAll(false);
  };

  // ── Multi-select filter click handler ────────────────────────────────────
  const handleFilterClick = (tabId) => {
    setActiveFilters(prev => {
      if (tabId === 'all') return new Set(['all']);
      const next = new Set(prev);
      next.delete('all');
      if (next.has(tabId)) {
        next.delete(tabId);
        if (next.size === 0) return new Set(['all']); // fall back to all when nothing selected
      } else {
        next.add(tabId);
      }
      return next;
    });
    setConfirmSellAll(false);
  };

  // ── Item passes the active filter ─────────────────────────────────────────
  // Runes are always excluded from the item inventory view.
  const itemPassesFilter = (item) => {
    if (!item) return true;
    if (item.slotType === 'rune') return false;
    if (activeFilters.has('all')) return true;
    if (activeFilters.has('set') && item.rarity === 'set') return true;
    return activeFilters.has(item.slotType);
  };

  // ── Item click handler ────────────────────────────────────────────────────
  const handleItemClick = (item, origIdx) => {
    if (!item) return;
    if (!itemPassesFilter(item)) return;
    if (lockMode) {
      onLock?.(origIdx);
    } else if (sellMode) {
      if (item.locked) return; // locked items can't be sold
      onSell(item, origIdx);
    } else {
      onEquip(item, origIdx);
    }
  };

  // ── Sell All: two-click confirm flow ──────────────────────────────────────
  const handleSellAllClick = () => {
    if (!confirmSellAll) {
      setConfirmSellAll(true);  // first click → arm confirm state
    } else {
      onSellAll([...activeFilters]); // second click → execute (locked items skipped in hook)
      setConfirmSellAll(false);
    }
  };

  // Count items that would be sold (locked items excluded)
  const sellableCount = inventory.filter(item =>
    item && !item.locked && itemPassesFilter(item)
  ).length;

  const isAllFilter = activeFilters.has('all');
  const filterLabel = isAllFilter ? '' : ` ${[...activeFilters].join('/')}`;

  return (
    <div className="inventory-section">
      {/* ── Header row ────────────────────────────────────────────────── */}
      <div className="inventory-header">
        <h3 className="inventory-title">
          Inventory <span className="inventory-count">{filled}/400</span>
        </h3>
        <div className="inventory-header-right">
          {/* Sort toggles */}
          <button
            className={`inv-sort-btn${sortMode === 'rarity' ? ' inv-sort-btn--active' : ''}`}
            onClick={() => toggleSort('rarity')}
            title="Sort by rarity"
          >
            {sortMode === 'rarity' ? '✦ Rarity' : '⬦ Rarity'}
          </button>
          <button
            className={`inv-sort-btn${sortMode === 'level' ? ' inv-sort-btn--active' : ''}`}
            onClick={() => toggleSort('level')}
            title="Sort by level"
          >
            {sortMode === 'level' ? '▲ Level' : '⬦ Level'}
          </button>

          {/* Lock mode toggle */}
          <button
            className={`inv-lock-btn${lockMode ? ' inv-lock-btn--active' : ''}`}
            onClick={toggleLockMode}
            title="Lock items to protect them from selling"
          >
            {lockMode ? '🔐 Locking' : '🔓 Lock'}
          </button>

          {sellMode && (
            <span className="inventory-gold-balance">💰 {totalGold}g</span>
          )}
          <button
            className={`sell-mode-btn${sellMode ? ' sell-mode-btn--active' : ''}`}
            onClick={toggleSellMode}
          >
            {sellMode ? '✅ Selling' : '💰 Sell'}
          </button>
        </div>
      </div>

      {/* ── Filter tabs (multi-select) ────────────────────────────────── */}
      <div className="inventory-filters">
        {FILTER_TABS.map(tab => (
          <button
            key={tab.id}
            className={`inv-filter-btn${activeFilters.has(tab.id) ? ' inv-filter-btn--active' : ''}`}
            onClick={() => handleFilterClick(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Sell-mode action bar (Sell All) ───────────────────────────── */}
      {sellMode && (
        <div className="sell-all-bar">
          <span className="sell-all-count">
            {sellableCount} item{sellableCount !== 1 ? 's' : ''} in view
          </span>
          <button
            className={`sell-all-btn${confirmSellAll ? ' sell-all-btn--confirm' : ''}`}
            onClick={handleSellAllClick}
            disabled={sellableCount === 0}
          >
            {confirmSellAll
              ? `⚠️ Confirm sell all (${sellableCount})`
              : `🗑 Sell All${filterLabel}`}
          </button>
        </div>
      )}

      {/* ── Grid ──────────────────────────────────────────────────────── */}
      <div className="inventory-grid">
        {packedDisplayItems.map(({ item, origIdx }, visualIdx) => {
          const key = origIdx !== null ? origIdx : `pad-${visualIdx}`;
          const price = item && sellMode ? getSellPrice(item) : null;
          const tooltipSide = (visualIdx % 10) < 3 ? 'right' : 'center';

          return (
            <ItemSlot
              key={key}
              item={item}
              index={origIdx}
              onEquip={handleItemClick}
              sellMode={sellMode && !!item && !item.locked}
              sellPrice={price}
              lockMode={lockMode && !!item}
              playerLevel={playerLevel}
              equippedSetCount={item?.setId ? (setCountMap[item.setId] ?? 0) : undefined}
              tooltipSide={tooltipSide}
            />
          );
        })}
      </div>

      {sellMode && (
        <p className="sell-mode-hint">
          Click any item to sell it · or use Sell All to clear the current filter.
        </p>
      )}
    </div>
  );
}
